import React, { useEffect, useState } from 'react'

import useTranslation from '@/Hooks/UseTranslation'
import Modal from '@/Components/Modal/Modal'

import './DeleteProjectModal.scss'

interface Props {
  show: boolean
  projectName: string
  onClose: () => void
  onConfirm: () => Promise<boolean>
  onArchive: () => Promise<boolean>
}

const DeleteProjectModal = ({ show, projectName, onClose, onConfirm, onArchive }: Props) => {

  const { t } = useTranslation()

  const [ confirmation, setConfirmation ] = useState('')
  const [ processing, setProcessing ] = useState<'delete' | 'archive' | null>(null)
  const [ error, setError ] = useState('')

  useEffect(() => {

    if (!show) return

    setConfirmation('')
    setProcessing(null)
    setError('')
  }, [ show, projectName ])

  const matchesProjectName = confirmation.trim() === projectName.trim()
  const canDelete = matchesProjectName && !processing

  const handleClose = () => {

    if (!processing) onClose()
  }

  const handleDelete = async () => {

    if (!canDelete) return

    setProcessing('delete')
    setError('')

    try {

      const deleted = await onConfirm()

      if (deleted) {

        onClose()
        return
      }

      setError(t('HOME:DeleteProjectModal-error'))
    } catch (error) {

      setError(t('HOME:DeleteProjectModal-error'))
    } finally {

      setProcessing(null)
    }
  }

  const handleArchive = async () => {

    if (processing) return

    setProcessing('archive')
    setError('')

    try {

      const archived = await onArchive()

      if (archived) {

        onClose()
        return
      }

      setError(t('HOME:DeleteProjectModal-archiveError'))
    } catch (error) {

      setError(t('HOME:DeleteProjectModal-archiveError'))
    } finally {

      setProcessing(null)
    }
  }

  const footer = (
    <>
      <button
        type="button"
        className="btn btn-secondary"
        onClick={handleClose}
        disabled={!!processing}
      >
        {t('HOME:DeleteProjectModal-cancel')}
      </button>
      <button
        type="button"
        className="btn btn-danger"
        onClick={handleDelete}
        disabled={!canDelete}
      >
        {processing === 'delete' && (
          <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true" />
        )}
        {processing !== 'delete' && (
          <i className="fas fa-trash-alt mr-2" aria-hidden="true" />
        )}
        {processing === 'delete'
          ? t('HOME:DeleteProjectModal-deleting')
          : t('HOME:DeleteProjectModal-confirm')}
      </button>
    </>
  )

  return (
    <Modal
      show={show}
      title={`${t('HOME:DeleteProjectModal-title')} “${projectName}”`}
      onClose={handleClose}
      showCloseButton={!processing}
      footer={footer}
      backdrop={processing ? 'static' : true}
      keyboard={!processing}
      centered
      className="delete-project-modal"
    >
      <div className="delete-project-modal__warning">
        <i className="fas fa-exclamation-triangle" aria-hidden="true" />
        <p>{t('HOME:DeleteProjectModal-warning')}</p>
      </div>

      <div className="delete-project-modal__archive-option">
        <span>{t('HOME:DeleteProjectModal-archiveHint')}</span>
        <button
          type="button"
          className="btn btn-link"
          onClick={handleArchive}
          disabled={!!processing}
        >
          {processing === 'archive' && (
            <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true" />
          )}
          {processing !== 'archive' && (
            <i className="fas fa-archive mr-2" aria-hidden="true" />
          )}
          {processing === 'archive'
            ? t('HOME:DeleteProjectModal-archiving')
            : t('HOME:DeleteProjectModal-archive')}
        </button>
      </div>

      <div className="form-group mb-0">
        <label htmlFor="delete-project-confirmation">
          {t('HOME:DeleteProjectModal-instruction')}
        </label>
        <div className="input-group">
          <input
            id="delete-project-confirmation"
            className={`form-control${matchesProjectName ? ' is-valid' : ''}`}
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            disabled={!!processing}
            placeholder={`${t('HOME:DeleteProjectModal-placeholder')} ${projectName}`}
            aria-describedby="delete-project-confirmation-feedback"
            autoComplete="off"
            autoFocus
          />
          {matchesProjectName && (
            <div className="input-group-append" aria-hidden="true">
              <span className="input-group-text delete-project-modal__match-icon">
                <i className="fas fa-check" />
              </span>
            </div>
          )}
        </div>
        <div id="delete-project-confirmation-feedback" className="delete-project-modal__feedback" aria-live="polite">
          {matchesProjectName ? t('HOME:DeleteProjectModal-match') : ''}
        </div>
      </div>

      {error && (
        <div className="alert alert-danger mt-3 mb-0" role="alert">
          {error}
        </div>
      )}
    </Modal>
  )
}

export default DeleteProjectModal
